import type { Identifier } from "react-admin";
import {
  CommonStatus,
  type AuditFields,
  type ClaimantRef,
  type ClientRef,
  type CommonFields,
  type PartnerRef,
  type PolicyRef,
} from "./common";
import { DateYMDString } from "./date";
import { ClaimItem } from "./claimItem";

// ==================== ENUMS & CONSTANTS ====================

export enum ViewByClaimType {
  CLAIM_COUNT = "claimCount",
  CLAIM_AMOUNT = "claimAmount",
}

// Stop-loss claim types
export const STOPLOSS_CLAIM_TYPES = ["medical", "rx"] as const;
export type StoplossClaimType = (typeof STOPLOSS_CLAIM_TYPES)[number];

// Stop-loss claim statuses
export const StoplossClaimStatus = {
  NEW: CommonStatus.NEW,
  IN_PROCESS: CommonStatus.IN_PROCESS,
  SUBMITTED: CommonStatus.SUBMITTED,
  PARTIALLY_REIMBURSED: CommonStatus.PARTIALLY_REIMBURSED,
  REIMBURSED: CommonStatus.REIMBURSED,
  DENIED: CommonStatus.DENIED,
  PENDED: CommonStatus.PENDED,
} as const;

export type StoplossClaimStatusType = (typeof StoplossClaimStatus)[keyof typeof StoplossClaimStatus];

// ==================== STOP-LOSS CLAIM ====================

/**
 * Base stop-loss claim interface - fields that are in both create and read
 */
interface StoplossClaimBase extends ClaimantRef {
  stoploss_claim_type: StoplossClaimType; // Required by backend
  submitted_date?: DateYMDString | null;
  estimated_future_expenses?: number;
  total_prior_submissions_ytd?: number;
  total_billed_amount?: number;
  total_allowed_amount?: number;
  claim_status: StoplossClaimStatusType;
  source_system_id?: string;
  approved?: boolean;
  total_amount_accel_funded?: number;
  notes?: string;
}

/**
 * Full stop-loss claim record from the API - includes server-managed fields
 */
export interface StoplossClaim extends StoplossClaimBase, CommonFields, AuditFields, PolicyRef, ClientRef, PartnerRef {
  // Calculated/server-managed fields (total_billed_amount and total_allowed_amount inherited from base)
  total_paid_amount?: number;
  final_cumulative_total?: number;
  first_service_date?: DateYMDString;
  last_service_date?: DateYMDString;
  first_paid_date?: DateYMDString;
  last_paid_date?: DateYMDString;

  // Read-only (#7244): set only when this claim is still owned by a record that
  // was merged and retired. The merge moves every claim, so this is a safety net
  // (pre-#7244 data, a repair); when set it names the LIVE record to show the
  // claim under, and claimant_name is that record's.
  merged_into_claimant_id_ref?: string;

  // Aura funding cache (US 5330 / 5310) — ISO timestamp of when Aegle Accel funded
  // the claim (stoploss_claims.aura_disbursed_at). Absent until the claim is funded.
  aura_disbursed_at?: string;

  // Two-track read model (US 5574 / #5378) — read-only projections for the
  // Specific Claims chips/columns (#5379). aura_submitted_at = Aura funding
  // requested; carrier_submitted_at = marked submitted to carrier. Absent = that
  // track hasn't been actioned. (aura_receivable_status lives in the #5331 block
  // below, shipped by that story.)
  aura_submitted_at?: string;
  carrier_submitted_at?: string;

  // Administering vendor (TPA/RX) that managed this roll-up (US 5324, Option B).
  // Absent/empty = unassigned. Drives per-TPA exhibit segmentation (#5326).
  vendor_id_ref?: string;

  // Populated associations
  claim_items?: ClaimItem[];
  source_documents?: Identifier[];
  claim_items_count?: number;

  // Aura submission state (US 5331). submitted_to_aura is the first-class flag
  // (true once the funding request is pushed), replacing the prior
  // total_amount_accel_funded > 0 proxy. Read-only, populated on the list and detail reads.
  submitted_to_aura: boolean;
  aura_receivable_id?: number | null;
  aura_receivable_status?: string | null;
}

/**
 * Payload for creating a new stop-loss claim
 * NOTE: claim_items are NOT included here - they must be created separately
 * after the claim is created, using the claim's ID as stoploss_claim_id_ref
 */
export interface StoplossClaimCreate extends StoplossClaimBase {}

/**
 * Payload for updating a stop-loss claim - all fields optional except id
 */
export interface StoplossClaimUpdate extends Partial<StoplossClaimBase> {
  id: Identifier;
  // Required fields from StoplossClaimBase become optional in updates
  stoploss_claim_type?: StoplossClaimType;
  claim_status?: StoplossClaimStatusType;
}
